import { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/Button';

interface Conversation {
  id: string;
  name: string | null;
  status: 'active' | 'archived' | 'deleted';
  created_at: string;
  updated_at: string;
  last_activity_at: string;
}

interface ConversationHeaderProps {
  conversation: Conversation;
  participantCount: number;
  onAddParticipant: () => void;
  onToggleParticipantPanel: () => void;
  onUpdateName?: (name: string) => Promise<void>;
}

export function ConversationHeader({
  conversation,
  participantCount,
  onAddParticipant,
  onToggleParticipantPanel,
  onUpdateName,
}: ConversationHeaderProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(conversation.name || 'Untitled');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Update editName when conversation changes
  useEffect(() => {
    setEditName(conversation.name || 'Untitled');
  }, [conversation.name]);

  const handleStartEdit = () => {
    if (onUpdateName) {
      setEditName(conversation.name || 'Untitled');
      setIsEditing(true);
    }
  };

  const handleSave = async () => {
    if (!onUpdateName) return;

    const trimmedName = editName.trim() || 'Untitled';
    if (trimmedName === conversation.name) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSaving(true);
      await onUpdateName(trimmedName);
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update name:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditName(conversation.name || 'Untitled');
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div className="p-4 bg-slate-800 border-b border-slate-700">
      <div className="flex justify-between items-start max-w-4xl mx-auto">
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleSave}
                disabled={isSaving}
                className="text-xl font-semibold text-white bg-slate-700 border border-slate-600 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full max-w-md"
                data-testid="conversation-name-input"
              />
              {isSaving && <span className="text-slate-400 text-sm">Saving...</span>}
            </div>
          ) : (
            <div className="flex items-center gap-2 group">
              <h2 className="text-xl font-semibold text-white truncate">
                {conversation.name || 'Untitled'}
              </h2>
              {onUpdateName && (
                <button
                  onClick={handleStartEdit}
                  className="p-1 text-slate-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Rename conversation"
                  data-testid="edit-conversation-name-button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                  </svg>
                </button>
              )}
            </div>
          )}
          <p className="text-sm text-slate-400 mt-1">
            Created {formatDate(conversation.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onToggleParticipantPanel}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-300 hover:text-white hover:bg-slate-700 rounded transition-colors"
            title="Show participants"
            data-testid="toggle-participant-panel"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <span>{participantCount}</span>
          </button>
          <Button onClick={onAddParticipant} variant="primary" size="sm">
            + Add Participant
          </Button>
        </div>
      </div>
    </div>
  );
}
